import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, CheckCircle2, Clock, XCircle, FileText, ArrowLeft, RefreshCw } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import html2pdf from 'html2pdf.js';
import { ensureGujaratiFontReady } from '../utils/gujFont';

export default function TrackApplication() {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const [searchNumber, setSearchNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [appData, setAppData] = useState(null);
  const [error, setError] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Auto-search if application number is provided in URL:
  //   - Path param  (preferred, public deep link): /track/KO1007
  //   - Query param (legacy fallback):             /track?track=KO1007
  useEffect(() => {
    let trackNo = trackId || null;
    if (!trackNo) {
      const params = new URLSearchParams(window.location.search);
      trackNo = params.get('track');
    }
    if (trackNo) {
      setSearchNumber(trackNo);
      fetchStatus(trackNo);
    } else {
      // clean state when navigating back to /track without an id
      setAppData(null);
      setError(null);
    }
  }, [trackId]);

  const fetchStatus = async (appNo) => {
    if (!appNo) return;
    setLoading(true);
    setError(null);
    setAppData(null);
    
    try {
      // Public status alias — works without auth and for any user's KO id.
      const res = await axios.get(`${API_URL}/api/applications/status/${appNo}`);
      setAppData(res.data);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError('અરજી નંબર મળ્યો નથી. કૃપા કરીને યોગ્ય નંબર દાખલ કરો.');
      } else {
        setError('સ્થિતિ તપાસવામાં ભૂલ આવી. ફરી પ્રયાસ કરો.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const cleaned = (searchNumber || '').trim();
    if (!cleaned) return;
    // Push /track/<id> so the URL reflects the search and deep-links stay shareable.
    if (cleaned !== trackId) {
      navigate(`/track/${cleaned}`);
    } else {
      // Same id — just re-fetch.
      fetchStatus(cleaned);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans flex flex-col">
      <Header />
      <main className="flex-1 w-full bg-[#F8FAFC]">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-[#E2E8F0] mt-6 max-w-3xl mx-auto mb-10">
          <h2 className="text-xl font-bold text-[#172554] text-gujarati mb-4 text-center border-b pb-4">
        અરજીની સ્થિતિ તપાસો (Track Application)
      </h2>

      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-8">
        <input 
          type="text"
          placeholder="અરજી નંબર દાખલ કરો (દા.ત. KO1001)"
          className="flex-1 border border-[#E2E8F0] rounded p-3 focus:outline-none focus:border-[#172554] text-gujarati"
          value={searchNumber}
          onChange={(e) => setSearchNumber(e.target.value)}
          required
        />
        <button 
          type="submit" 
          disabled={loading}
          className="bg-[#172554] text-white px-6 py-3 rounded font-bold text-gujarati flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
        >
          {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          તપાસો
        </button>
      </form>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded text-center font-bold text-gujarati">
          {error}
        </div>
      )}

      {appData && (
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg overflow-hidden">
          <div className="bg-[#172554] p-4 text-white flex justify-between items-center flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold">અરજી નંબર: {appData.application_number}</span>
              {(appData.application_type || '').includes('Urgent') && (
                <span 
                  data-testid={`track-urgent-ribbon-${appData.application_number}`}
                  className="inline-flex items-center gap-1 bg-gradient-to-r from-[#F5B800] to-[#FFC928] text-[#172554] px-2.5 py-0.5 rounded-full text-[11px] font-extrabold text-gujarati shadow-sm ring-1 ring-[#F5B800]/50 uppercase tracking-wide"
                  title="તત્કાલ અરજી (Urgent)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z"/></svg>
                  તત્કાલ
                </span>
              )}
            </div>
            <span className="text-xs bg-white/20 px-2 py-1 rounded">
              {new Date(appData.created_at).toLocaleDateString()}
            </span>
          </div>
          
          <div className="p-6 flex flex-col gap-6">
            
            {/* Professional Visual Progress Bar
                — 5 happy-path steps: અરજી મળી → દસ્તાવેજ ચકાસણી → સરકારી પ્રક્રિયા → અંતિમ મંજૂરી → અરજી પૂર્ણ
                — Cancelled shows the whole bar red with a final X-circle. */}
            {(() => {
              const s = appData.status;
              const isCancelled = s === 'Cancelled';
              // Progress index on happy path (0-based → 4 for Complete). We map existing statuses onto the 5-step timeline.
              const idx = ({
                'Submitted': 0,
                'Pending': 1,
                'Inprocess': 2,
                'Approval': 3,
                'Complete': 4,
              })[s] ?? (isCancelled ? 4 : 0);
              // Build a map: status -> earliest changed_at from status_history so each visual step shows when it was reached.
              const historyMap = {};
              (appData.status_history || []).forEach(h => {
                if (h && h.status && h.changed_at && !historyMap[h.status]) {
                  historyMap[h.status] = h.changed_at;
                }
              });
              // Fallback: first step uses application created_at if no explicit history entry.
              if (!historyMap['Submitted'] && appData.created_at) historyMap['Submitted'] = appData.created_at;
              const fmtStamp = (iso) => {
                if (!iso) return '';
                try {
                  const dt = new Date(iso);
                  return dt.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                } catch { return ''; }
              };
              const steps = [
                { label: 'અરજી મળી', sub: 'Received', Icon: Clock, statusKey: 'Submitted' },
                { label: 'દસ્તાવેજ ચકાસણી', sub: 'Verification', Icon: FileText, statusKey: 'Pending' },
                { label: 'સરકારી પ્રક્રિયા', sub: 'Processing', Icon: RefreshCw, spinning: s === 'Inprocess', statusKey: 'Inprocess' },
                { label: 'અંતિમ મંજૂરી', sub: 'Approval', Icon: CheckCircle2, statusKey: 'Approval' },
                { label: isCancelled ? 'રદ / રિફંડ' : 'અરજી પૂર્ણ', sub: isCancelled ? 'Cancelled' : 'Completed', Icon: isCancelled ? XCircle : CheckCircle2, terminal: true, statusKey: isCancelled ? 'Cancelled' : 'Complete' },
              ];
              const fillPct = isCancelled ? 100 : (idx / (steps.length - 1)) * 100;
              const fillColour = isCancelled ? 'bg-red-500' : 'bg-[#172554]';
              const nodeColour = (i) => {
                if (isCancelled) {
                  if (i === 0) return 'bg-red-500 border-red-500 text-white';
                  if (i === steps.length - 1) return 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-500/40';
                  return 'bg-white border-gray-200 text-gray-400';
                }
                if (i < idx) return 'bg-[#172554] border-[#172554] text-white';
                if (i === idx) {
                  if (i === steps.length - 1) return 'bg-green-600 border-green-600 text-white';
                  return 'bg-[#F5B800] border-[#F5B800] text-[#172554] shadow-lg shadow-yellow-300/50';
                }
                return 'bg-white border-gray-200 text-gray-400';
              };
              const labelColour = (i) => {
                if (isCancelled) {
                  if (i === 0 || i === steps.length - 1) return 'text-red-600';
                  return 'text-gray-400';
                }
                if (i < idx) return 'text-[#172554]';
                if (i === idx) return i === steps.length - 1 ? 'text-green-600' : 'text-[#172554]';
                return 'text-gray-400';
              };
              return (
                <div className="w-full mb-4" data-testid="track-progress-bar">
                  <div className="flex items-start justify-between relative pt-1">
                    <div className="absolute left-4 right-4 top-5 h-1 bg-gray-200 z-0"></div>
                    <div
                      className={`absolute left-4 top-5 h-1 ${fillColour} z-0 transition-all duration-500`}
                      style={{ width: `calc((100% - 32px) * ${fillPct} / 100)` }}
                    ></div>
                    {steps.map((step, i) => {
                      const StepIcon = step.Icon;
                      const stamp = fmtStamp(historyMap[step.statusKey]);
                      const reached = isCancelled ? (i === 0 || i === steps.length - 1) : i <= idx;
                      return (
                        <div key={i} className="relative z-10 flex flex-col items-center flex-1" data-testid={`track-step-${i}`}>
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center border-4 ${nodeColour(i)}`}>
                            <StepIcon className={`w-4 h-4 ${step.spinning ? 'animate-spin' : ''}`} />
                          </div>
                          <span className={`text-[10px] sm:text-xs mt-2 font-bold text-center max-w-[80px] text-gujarati leading-tight ${labelColour(i)}`}>{step.label}</span>
                          <span className="text-[9px] text-[#94a3b8] mt-0.5">{step.sub}</span>
                          {reached && stamp && (
                            <span className={`mt-1 text-[9px] font-semibold ${isCancelled && (i === 0 || i === steps.length - 1) ? 'text-red-500' : 'text-[#172554]'}`} data-testid={`track-step-time-${i}`}>
                              {stamp}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#172033]">
              <div><span className="text-[#64748B] text-xs block mb-1">અરજદારનું નામ:</span> <b className="text-gujarati">{appData.full_name}</b></div>
              <div><span className="text-[#64748B] text-xs block mb-1">શ્રેણી:</span> <b className="text-gujarati">{appData.category_name}</b></div>
              <div><span className="text-[#64748B] text-xs block mb-1">સેવા:</span> <b className="text-gujarati">{appData.sub_category_name}</b></div>
              <div><span className="text-[#64748B] text-xs block mb-1">ચુકવણીની સ્થિતિ:</span> 
                 <b className={appData.payment_status === 'Completed' ? 'text-green-600' : 'text-red-600'}>
                   {appData.payment_status === 'Completed' ? 'પૂર્ણ (Completed)' : (appData.payment_status === 'Failed' ? 'નિષ્ફળ (Failed)' : 'બાકી છે (Pending)')}
                 </b>
              </div>
              <div><span className="text-[#64748B] text-xs block mb-1">અરજીની સ્થિતિ:</span> 
                 <b className={appData.status === 'Cancelled' ? 'text-red-600' : 'text-[#172554]'}>
                   {appData.status === 'Submitted' && 'સબમિટ થઈ ગઈ'}
                   {appData.status === 'Pending' && 'પેન્ડિંગ (Pending)'}
                   {appData.status === 'Inprocess' && 'પ્રક્રિયા ચાલુ છે (In Process)'}
                   {appData.status === 'Approval' && 'અંતિમ મંજૂરી (Approval)'}
                   {appData.status === 'Complete' && 'પૂર્ણ (Complete)'}
                   {appData.status === 'Cancelled' && 'રદ / રિફંડ (Cancelled / Refund)'}
                 </b>
              </div>
          <div className="bg-[#FFF8DB]/50 border border-[#F5B800]/30 rounded p-3 mt-1">
            <span className="text-[#64748B] text-xs block mb-1 font-bold">રીમાર્ક (Remark):</span>
            <p className="text-sm text-[#172554] text-gujarati italic">
              {(() => {
                const history = appData.status_history || [];
                const latestWithRemark = [...history].reverse().find(h => h && h.remark && h.remark.trim() !== '');
                return latestWithRemark ? latestWithRemark.remark : 'N/A';
              })()}
            </p>
          </div>
            </div>

            {appData.assigned_executive?.name && (
              <div className="bg-gradient-to-r from-[#172554] to-[#0f1a3a] rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-white" data-testid={`track-exec-card-${appData.application_number}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-[#F5B800] flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#172554]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#F5B800] font-bold">તમારા એક્ઝિક્યુટિવ (Your Executive)</div>
                    <div className="font-bold text-white text-gujarati">{appData.assigned_executive.name}</div>
                    <div className="text-xs text-white/80 text-gujarati">{appData.assigned_executive.designation}</div>
                  </div>
                </div>
                {appData.assigned_executive.contact_number && (
                  <a
                    href={`tel:${appData.assigned_executive.contact_number}`}
                    className="inline-flex items-center gap-2 bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] font-extrabold px-4 py-2.5 rounded transition-colors"
                    data-testid={`track-call-exec-${appData.application_number}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    કૉલ કરો · {appData.assigned_executive.contact_number}
                  </a>
                )}
              </div>
            )}
            
            <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-[#E2E8F0]">
              <button 
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API_URL}/api/applications/receipt/${appData.application_number}`);
                    const element = document.createElement('div');
                    element.innerHTML = res.data;
                    
                    // Hide print button in PDF
                    const printBtn = element.querySelector('.print-btn');
                    if (printBtn) printBtn.style.display = 'none';

                    // Load Gujarati TTF before html2canvas rasterizes.
                    await ensureGujaratiFontReady();

                    const opt = {
                      margin:       0.5,
                      filename:     `Receipt_${appData.application_number}.pdf`,
                      image:        { type: 'jpeg', quality: 0.98 },
                      html2canvas:  { scale: 2, useCORS: true, allowTaint: true },
                      jsPDF:        { unit: 'in', format: 'letter', orientation: 'portrait' }
                    };
                    
                    html2pdf().from(element).set(opt).save();
                  } catch(e) {
                    console.error("PDF download failed", e);
                  }
                }}
                className="flex-1 min-w-[160px] bg-white border border-[#172554] text-[#172554] font-bold py-2 rounded hover:bg-gray-50 text-gujarati flex items-center justify-center gap-2"
                data-testid="track-download-receipt-btn"
              >
                <FileText className="w-4 h-4" /> રસીદ ડાઉનલોડ કરો
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(
                      `${API_URL}/api/applications/${appData.application_number}/pack`,
                      { responseType: 'blob' }
                    );
                    const client = (appData.full_name || appData.user_phone || 'Client').replace(/[^A-Za-z0-9\u0A80-\u0AFF_-]+/g, '_') || 'Client';
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${appData.application_number}_${client}_Complete_Application.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => URL.revokeObjectURL(url), 4000);
                  } catch (e) {
                    console.error(e);
                  }
                }}
                className="flex-1 min-w-[180px] bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] font-extrabold py-2 rounded text-gujarati border-2 border-[#F5B800] flex items-center justify-center gap-2 shadow-sm"
                data-testid="track-download-pack-btn"
                title="Download Complete Application PDF (form + documents + receipt)"
              >
                <FileText className="w-4 h-4" /> ડાઉનલોડ અરજી ફોર્મ
              </button>
            </div>

            {appData.status === 'Complete' && Array.isArray(appData.processed_documents) && appData.processed_documents.length > 0 && (
              <div className="mt-2 pt-4 border-t border-[#E2E8F0]">
                <h4 className="font-bold text-[#172554] text-gujarati mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  પ્રોસેસ કરેલા દસ્તાવેજો ડાઉનલોડ કરો (Download Processed Documents)
                </h4>
                <div className="flex flex-col gap-2">
                  {appData.processed_documents.map(doc => (
                    <a
                      key={doc.id}
                      href={doc.download_url || `${API_URL}/api/public/files/${doc.storage_path}`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center justify-between bg-green-50 border border-green-200 hover:bg-green-100 rounded p-3 transition-colors"
                      data-testid={`user-processed-doc-${doc.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-5 h-5 text-green-700 shrink-0" />
                        <span className="text-sm font-bold text-[#172554] truncate">{doc.original_filename}</span>
                      </div>
                      <span className="text-xs text-green-700 font-bold flex items-center gap-1 shrink-0">
                        <FileText className="w-3 h-3" /> ડાઉનલોડ
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      )}
      </div>
      </main>
      <Footer />
    </div>
  );
}